"use client";

import { getCookie, setCookie, deleteCookie } from "cookies-next/client";
import { EntryPoint } from "../../types/api";

// In-memory storage for client
let memoryToken: string | null = null;

export class TokenManager {
  // Get token from client storage
  static getToken(): string | null {
    // Always check memory first for performance
    if (memoryToken) {
      return memoryToken;
    }

    const token = getCookie("token");
    if (token) {
      memoryToken = token; // Cache in memory
      return token;
    }

    return null;
  }

  // Set token in client storage
  static setToken(token: string): void {
    // Always set in memory first
    memoryToken = token;

    // Set cookie
    setCookie("token", token, {
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      maxAge: 60 * 60 * 24 * 30, // 30 days
      path: "/",
    });

    console.log("🔑 Token set in client cookie");
  }

  // Clear token from client storage
  static clearToken(): void {
    memoryToken = null;
    deleteCookie("token");
  }

  // Check if token exists
  static hasToken(): boolean {
    const token = this.getToken();
    return !!token;
  }

  // Client ID (number that starts from 0 and goes up)
  static getClientId(): number {
    const value = getCookie("clientId");
    const parsed = value ? parseInt(String(value), 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  static setClientId(clientId: number): void {
    const safe = Number.isFinite(clientId) && clientId >= 0 ? clientId : 0;
    setCookie("clientId", String(safe), {
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      maxAge: 60 * 60 * 24 * 365, // 1 year
      path: "/",
    });
  }

  static incrementClientId(): number {
    const next = this.getClientId() + 1;
    this.setClientId(next);
    return next;
  }

  // Entry point tracking
  static getEntryPoint(): EntryPoint | null {
    const value = getCookie("entryPoint");
    return (value as EntryPoint) || null;
  }

  static setEntryPoint(entryPoint: EntryPoint): void {
    setCookie("entryPoint", entryPoint, {
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
      maxAge: 60 * 60 * 24 * 7, // 7 days
      path: "/",
    });
  }

  // Clear all session data
  static clearAll(): void {
    this.clearToken();
    deleteCookie("clientId");
    deleteCookie("entryPoint");
  }
}
